import { AlertTriangle, CheckCircle2, FileText, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useImportWorkOrders } from "@/hooks/use-work-orders";

type UploadState = "idle" | "drag" | "selected" | "uploading" | "success" | "error" | "partial";

interface ImportResult {
  added: number;
  updated: number;
  skipped: number;
  errorRows: { row: number; reason: string }[];
  msg?: string;
}

interface ImportWorkOrdersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImportWorkOrdersDialog({ open, onOpenChange }: ImportWorkOrdersDialogProps) {
  const [uploadState, setUploadState] = useState<UploadState>("idle");
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const importWOs = useImportWorkOrders();

  useEffect(() => {
    if (!open) {
      setUploadState("idle");
      setFile(null);
      setProgress(0);
      setImportResult(null);
    }
  }, [open]);

  function handleFile(f: File | undefined) {
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) {
      setUploadState("error");
      setImportResult({
        added: 0,
        updated: 0,
        skipped: 0,
        errorRows: [],
        msg: "Only .xlsx or .xls files are accepted",
      });
      return;
    }
    setFile(f);
    setUploadState("selected");
    setImportResult(null);
  }

  function startUpload() {
    setUploadState("uploading");
    setProgress(0);
    const timer = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          clearInterval(timer);
          importWOs.mutate(
            { records: [] },
            {
              onSuccess: (result) => {
                const r = result as {
                  added: number;
                  updated: number;
                  skipped: number;
                  errorRows: { row: number; reason: string }[];
                };
                if (r.skipped > 0) {
                  setUploadState("partial");
                  setImportResult({
                    added: r.added,
                    updated: r.updated,
                    skipped: r.skipped,
                    errorRows: r.errorRows,
                  });
                } else {
                  setUploadState("success");
                  setImportResult({
                    added: r.added,
                    updated: r.updated,
                    skipped: 0,
                    errorRows: [],
                  });
                }
              },
              onError: () => {
                setUploadState("error");
                setImportResult({
                  added: 0,
                  updated: 0,
                  skipped: 0,
                  errorRows: [],
                  msg: "Import failed. Please try again.",
                });
              },
            },
          );
          return 100;
        }
        return p + 8;
      });
    }, 80);
  }

  function reset() {
    setUploadState("idle");
    setFile(null);
    setProgress(0);
    setImportResult(null);
  }

  if (!open) return null;

  const isPending = uploadState === "uploading";

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (!isPending) onOpenChange(false);
      }}
    >
      <div
        className="modal wo-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal__header">
          <h2 className="modal__title">Import WO Log</h2>
        </div>
        <div className="modal__body">
          {(uploadState === "idle" || uploadState === "drag") && (
            <div
              className={`upload-zone${uploadState === "drag" ? " is-drag" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                if (uploadState !== "drag") setUploadState("drag");
              }}
              onDragLeave={() => setUploadState("idle")}
              onDrop={(e) => {
                e.preventDefault();
                handleFile(e.dataTransfer.files[0]);
              }}
              onClick={() => inputRef.current?.click()}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <div className="upload-zone__icon">
                <Upload size={20} />
              </div>
              <div className="upload-zone__title">
                {uploadState === "drag"
                  ? "Drop to upload"
                  : "Drag your WO Log here, or click to browse"}
              </div>
              <div className="upload-zone__sub">Accepts .xlsx and .xls files</div>
            </div>
          )}

          {uploadState === "selected" && (
            <div className="upload-zone is-uploading">
              <div className="upload-zone__icon" style={{ color: "var(--navy)" }}>
                <FileText size={20} />
              </div>
              <div className="upload-zone__title">{file?.name}</div>
              <div className="upload-zone__sub">
                {((file?.size ?? 0) / 1024).toFixed(1)} KB · ready to import
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 14 }}>
                <button type="button" className="btn btn--secondary btn--sm" onClick={reset}>
                  Cancel
                </button>
                <button type="button" className="btn btn--primary btn--sm" onClick={startUpload}>
                  Upload
                </button>
              </div>
            </div>
          )}

          {uploadState === "uploading" && (
            <div className="upload-zone is-uploading">
              <div className="upload-zone__icon">
                <Upload size={20} />
              </div>
              <div className="upload-zone__title">Importing work orders…</div>
              <div className="upload-zone__sub">{progress}% complete</div>
              <div className="progress">
                <div className="progress__bar" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          {uploadState === "success" && importResult && (
            <div className="upload-zone is-success">
              <div className="upload-zone__icon">
                <CheckCircle2 size={20} />
              </div>
              <div className="upload-zone__title">Import complete</div>
              <div className="upload-zone__sub">
                {importResult.added} records added · {importResult.updated} updated
              </div>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={reset}
                style={{ marginTop: 12 }}
              >
                Import another file
              </button>
            </div>
          )}

          {uploadState === "error" && (
            <div className="upload-zone is-error" onClick={() => inputRef.current?.click()}>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <div className="upload-zone__icon">
                <AlertTriangle size={20} />
              </div>
              <div className="upload-zone__title">
                {importResult?.msg ?? "Could not read the file"}
              </div>
              <div className="upload-zone__sub">
                Click to try again, or check the format matches the WO Log template.
              </div>
            </div>
          )}

          {uploadState === "partial" && importResult && (
            <div>
              <div className="upload-zone is-success">
                <div
                  className="upload-zone__icon"
                  style={{ color: "var(--amber)", borderColor: "#F5D9AC" }}
                >
                  <AlertTriangle size={20} />
                </div>
                <div className="upload-zone__title">
                  {importResult.added + importResult.updated} records imported
                </div>
                <div className="upload-zone__sub">
                  {importResult.skipped} rows had errors (shown below)
                </div>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={reset}
                  style={{ marginTop: 12 }}
                >
                  Import another file
                </button>
              </div>
              <details open style={{ marginTop: 14 }}>
                <summary
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer",
                    color: "var(--navy)",
                  }}
                >
                  Skipped rows ({importResult.errorRows.length})
                </summary>
                <div
                  style={{
                    marginTop: 8,
                    background: "var(--red-bg)",
                    border: "1px solid #F5C7C7",
                    borderRadius: 8,
                    padding: 10,
                    fontSize: 12,
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {importResult.errorRows.map((r) => (
                    <div key={String(r.row)} style={{ padding: "4px 0" }}>
                      Row {r.row}: {r.reason}
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}
        </div>

        <div className="modal__footer">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
